import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, Save, RotateCcw, ChevronDown, Mail, History, Eye, ArrowLeft } from "lucide-react";
import { PaidChargesList } from "@/components/PaidChargesList";
import { SignAlerts } from "@/components/SignAlerts";
import { DocPreviewDialog } from "@/components/DocPreviewDialog";
import { ContactDefault, CONTACT_KEYS } from "@/components/ContactEmailDefault";
import { DocHistory, saveBlob } from "@/components/DocHistory";
import { DocEmailDialog } from "@/components/DocEmailDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/AdminLayout";
import { api, apiError } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const Pick = ({ value, onChange, placeholder, items, testid }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="w-full min-w-0 bg-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-72">{items.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-option-${v}`}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const FieldsForm = ({ tpl, fields, setFields, isAdmin, emailInfo, setEmailInfo }) => (
  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="docs-fields">
    {tpl.fields.map((f) => (
      <div key={f.key} className="space-y-1.5">
        <Label htmlFor={`doc-${f.key}`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{f.label}</Label>
        <Input id={`doc-${f.key}`} value={fields[f.key] || ""} onChange={(e) => setFields((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`doc-field-${f.key}`} />
        {CONTACT_KEYS.includes(f.key) && isAdmin && emailInfo && (
          <ContactDefault tid={tpl.id} fieldKey={f.key} value={fields[f.key]} info={emailInfo} setInfo={setEmailInfo} setValue={(v) => setFields((s) => ({ ...s, [f.key]: v }))} />
        )}
      </div>
    ))}
  </div>
);

const MANUAL = ["confirmation", "member_no", "room_no", "juniors"];

const TextsEditor = ({ paras, overrides, setOverrides, isAdmin }) => (
  <div className="max-h-[560px] space-y-2 overflow-auto pr-1" data-testid="docs-texts">
    {paras.filter((p) => p.text.trim()).map((p) => (
      <div key={p.i} className="flex items-start gap-2">
        <Textarea rows={Math.min(6, Math.max(1, Math.ceil(p.text.length / 110)))} value={overrides[p.i] ?? p.text} disabled={p.field}
          title={p.field ? "Este texto se llena con los Datos del documento de arriba" : undefined}
          onChange={(e) => setOverrides((o) => ({ ...o, [p.i]: e.target.value }))}
          className={`text-sm ${p.field ? "bg-slate-50 text-slate-500" : ""} ${overrides[p.i] !== undefined && overrides[p.i] !== null ? "border-amber-300 bg-amber-50/40" : p.saved ? "border-violet-200" : ""}`} data-testid={`doc-text-${p.i}`} />
        {overrides[p.i] !== undefined && overrides[p.i] !== null && (
          <button type="button" title="Deshacer" className="mt-2 text-slate-400 hover:text-slate-700" data-testid={`doc-text-reset-${p.i}`}
            onClick={() => setOverrides((o) => { const n = { ...o }; delete n[p.i]; return n; })}><RotateCcw size={15} /></button>
        )}
        {isAdmin && p.saved && overrides[p.i] === undefined && (
          <button type="button" title="Volver al texto original (al guardar)" className="mt-2 text-violet-400 hover:text-violet-700" data-testid={`doc-text-unsave-${p.i}`}
            onClick={() => setOverrides((o) => ({ ...o, [p.i]: null }))}><RotateCcw size={15} /></button>
        )}
      </div>
    ))}
  </div>
);

export default function Docs() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [charges, setCharges] = useState([]);
  const [tid, setTid] = useState("certificado-es");
  const [cid, setCid] = useState("");
  const [fields, setFields] = useState({});
  const [emailInfo, setEmailInfo] = useState(null);
  const [paras, setParas] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [showTexts, setShowTexts] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [histKey, setHistKey] = useState(0);
  const [preview, setPreview] = useState({ open: false, pages: [], loading: false });
  const tpl = useMemo(() => templates.find((t) => t.id === tid), [templates, tid]);
  const tplCharges = useMemo(() => charges.filter((c) => c.hotel_slug === tpl?.hotel), [charges, tpl]);
  useEffect(() => { if (cid && tpl && !tplCharges.some((c) => c.id === cid)) { setCid(""); } }, [tpl, tplCharges, cid]);

  useEffect(() => {
    api.get("/docs/templates").then((r) => setTemplates(r.data)).catch((e) => toast.error(apiError(e).message));
  }, []);
  useEffect(() => {
    api.get("/docs/charges").then((r) => setCharges(r.data)).catch((e) => toast.error(apiError(e).message));
  }, [histKey]);
  useEffect(() => {
    if (!cid || !tplCharges.some((c) => c.id === cid)) return;
    setOverrides({});
    api.post(`/docs/${tid}/defaults`, { charge_id: cid })
      .then((r) => {
        setEmailInfo(Object.fromEntries(CONTACT_KEYS.map((k) => [k, { saved: !!r.data.contacts_saved?.[k], current: r.data.fields[k] }])));
        setFields((prev) => ({ ...r.data.fields, ...Object.fromEntries(MANUAL.filter((k) => prev[k]).map((k) => [k, prev[k]])) }));
      })
      .catch((e) => toast.error(apiError(e).message));
  }, [cid, tid]);
  useEffect(() => {
    if (!cid || !showTexts) return undefined;
    const h = setTimeout(() => { api.post(`/docs/${tid}/paragraphs`, { fields }).then((r) => setParas(r.data)); }, 300);
    return () => clearTimeout(h);
  }, [fields, tid, cid, showTexts]);
  const prevContacts = useRef({});
  const contactSig = CONTACT_KEYS.map((k) => fields[k] || "").join("|");
  useEffect(() => {
    const prevAll = prevContacts.current;
    prevContacts.current = Object.fromEntries(CONTACT_KEYS.map((k) => [k, (fields[k] || "").trim()]));
    const pairs = CONTACT_KEYS.map((k) => [prevAll[k], prevContacts.current[k]]).filter(([a, b]) => a && b && a !== b);
    if (!pairs.length) return;
    setOverrides((o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, typeof v === "string" ? pairs.reduce((s, [a, b]) => s.replace(new RegExp(a.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), b), v) : v])));
  }, [contactSig]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/docs/${tid}/pdf`, { charge_id: cid, fields, overrides }, { responseType: "blob" });
      const c = charges.find((x) => x.id === cid);
      saveBlob(r.data, `${tpl?.lang === "en" ? "Confirmation" : "Certificado"}-${(c?.guest_name || "huesped").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}.pdf`);
      setHistKey((k) => k + 1);
      toast.success("PDF descargado y guardado en el historial del cobro");
    } catch (e) { toast.error("No se pudo generar el PDF"); } finally { setBusy(false); }
  };
  const showPreview = async () => {
    setPreview({ open: true, pages: [], loading: true });
    try { const r = await api.post(`/docs/${tid}/preview`, { charge_id: cid, fields, overrides }); setPreview({ open: true, pages: r.data.pages, loading: false }); }
    catch (e) { toast.error("No se pudo generar la vista previa"); setPreview({ open: false, pages: [], loading: false }); }
  };
  const saveTexts = async () => {
    try { await api.put(`/docs/${tid}/texts`, { overrides }); toast.success("Textos guardados como predeterminados para esta plantilla"); setOverrides({}); api.post(`/docs/${tid}/paragraphs`, { fields }).then((r) => setParas(r.data)); }
    catch (e) { toast.error(apiError(e).message); }
  };

  return (
    <>
      <PageHeader title="Certificados" subtitle="Genera el certificado o la confirmación del hotel con los datos del cobro pagado y descárgalo en PDF." />
      <div className="mb-6 grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 md:grid-cols-2">
        <div className="min-w-0 space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Plantilla</Label>
          <Pick value={tid} onChange={setTid} placeholder="Elige plantilla" items={templates.map((t) => [t.id, t.name])} testid="docs-template" /></div>
        <div className="min-w-0 space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cobro pagado{tpl ? ` de ${tpl.brand}` : ""}</Label>
          <Pick value={cid} onChange={setCid} placeholder={tplCharges.length ? "Elige el cobro" : `No hay cobros pagados de ${tpl?.brand || "este hotel"}`} testid="docs-charge"
            items={tplCharges.map((c) => [c.id, `${c.guest_name || "Sin nombre"} · ${c.property_name || tpl?.brand || ""} · ${fmtMoney(c.amount_cents, c.currency)}`])} /></div>
      </div>
      {!tpl ? null : !cid ? (
        <div className="space-y-6">
          <SignAlerts key={histKey} onOpen={(id) => { const c = charges.find((x) => x.id === id); if (c && c.hotel_slug !== tpl.hotel) { const t = templates.find((x) => x.hotel === c.hotel_slug); if (t) setTid(t.id); } setCid(id); }} onChanged={() => setHistKey((k) => k + 1)} />
          <PaidChargesList charges={tplCharges} brand={tpl.brand} onPick={setCid} />
        </div>
      ) : (
        <div className="space-y-6">
          <Button variant="ghost" size="sm" onClick={() => setCid("")} data-testid="docs-back-to-list"><ArrowLeft size={15} className="mr-1.5" />Ver lista de ventas</Button>
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="mb-4 font-heading text-lg font-semibold text-slate-900">Datos del documento</h2>
            <FieldsForm tpl={tpl} fields={fields} setFields={setFields} isAdmin={user?.role === "admin"} emailInfo={emailInfo} setEmailInfo={setEmailInfo} />
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <button type="button" onClick={() => setShowTexts((s) => !s)} className="flex w-full items-center justify-between text-left" data-testid="docs-texts-toggle">
              <span><span className="block font-heading text-lg font-semibold text-slate-900">Textos del documento</span>
                <span className="text-sm text-slate-500">Edita cualquier texto (políticas, incluidos, contacto). En amarillo: cambios solo para este PDF. En morado: textos guardados por el administrador. Las líneas grises se llenan con los Datos del documento.</span></span>
              <ChevronDown className={`shrink-0 transition-transform ${showTexts ? "rotate-180" : ""}`} />
            </button>
            {showTexts && <div className="mt-4"><TextsEditor paras={paras} overrides={overrides} setOverrides={setOverrides} isAdmin={user?.role === "admin"} /></div>}
          </section>
          <div className="flex flex-wrap justify-end gap-3">
            {user?.role === "admin" && showTexts && <Button variant="outline" onClick={saveTexts} data-testid="docs-save-texts-btn"><Save size={16} className="mr-2" />Guardar textos como predeterminados</Button>}
            <Button variant="outline" onClick={showPreview} disabled={preview.loading} data-testid="docs-preview-btn"><Eye size={16} className="mr-2" />Vista previa</Button>
            <Button variant="outline" onClick={() => setEmailOpen(true)} data-testid="docs-email-btn"><Mail size={16} className="mr-2" />Enviar por correo</Button>
            <Button onClick={download} disabled={busy} className="bg-[#2F5BFF] hover:bg-[#1F45E0]" data-testid="docs-download-btn"><FileDown size={16} className="mr-2" />{busy ? "Generando PDF…" : "Descargar PDF"}</Button>
          </div>
          <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="docs-history-section">
            <h2 className="mb-2 flex items-center gap-2 font-heading text-lg font-semibold text-slate-900"><History size={18} />Documentos generados de este cobro</h2>
            <DocHistory charge={charges.find((x) => x.id === cid)} refreshKey={histKey} />
          </section>
          <DocEmailDialog open={emailOpen} onOpenChange={setEmailOpen} tid={tid} charge={charges.find((x) => x.id === cid)}
            filename={`${tpl.lang === "en" ? "Confirmation" : "Certificado"}-${(charges.find((x) => x.id === cid)?.guest_name || "huesped").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}.pdf`}
            payload={{ url: `/docs/${tid}/send`, body: { charge_id: cid, fields, overrides } }} onSent={() => setHistKey((k) => k + 1)} />
          <DocPreviewDialog open={preview.open} onOpenChange={(o) => setPreview((p) => ({ ...p, open: o }))} pages={preview.pages} loading={preview.loading} />
        </div>
      )}
    </>
  );
}
